import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { setStatusBarColor } from '~/app/helpers/statusBar';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import Icon from '~/app/common/Icon';

import { useStoreActions, useStoreState } from '~/store';

import bardeLogo from '~/assets/images/barde-logo.png';

import Title from './Title';
import UserDropdown from './UserDropdown';
import Favorite from './Favorite'

import classes from './style.module.scss';
import LinkWrapper from '~/app/common/LinkWrapper';
import { REQUESTS_ROUTES } from '~/notifications/Router';
import classNames from 'classnames';
import IssueOptions from './IssueOptions';
import IssueCategorySearch from './IssueCategorySearch';
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { useNotificationContext } from '~/app/hooks/useNotificationContext';
import useTranslation from '~/app/hooks/useTranslation';

export type AppHeaderComponent = React.FC

export const AppHeader: AppHeaderComponent = () => {
  const history = useHistory();
  const { useNotificationState, useNotificationAction } = useNotificationContext();

  const title = useStoreState(state => state.app.appHeader.title);
  const backLink = useStoreState(state => state.app.appHeader.backLink);
  const backLinkIcon = useStoreState(state => state.app.appHeader.backLinkIcon);
  const chatIcon = useStoreState(state => state.app.appHeader.chatIcon);
  const notificationIcon = useStoreState(state => state.app.appHeader.notificationIcon);
  const menuIcon = useStoreState(state => state.app.appHeader.menuIcon);
  const searchIcon = useStoreState(state => state.app.appHeader.searchIcon);
  const showUser = useStoreState(state => state.app.appHeader.showUser);
  const machineId = useStoreState(state => state.app.appHeader.machineId);
  const projectId = useStoreState(state => state.app.appHeader.projectId);
  const parentFolderId = useStoreState(state => state.app.appHeader.parentFolderId);
  const isIssueDetailPage = useStoreState((state) => state.app.appHeader.isIssueDetailPage);

  const setIsMenuOpen = useStoreActions(actions => actions.app.sidebar.setOpen);
  const notification = useStoreState(state => state.app.appHeader.notification);
  const search = useStoreState(state => state.app.appHeader.search);
  const issueCategorySearch = useStoreState(state => state.app.appHeader.issueCategorySearch);
  const isCategorySearchOpen = useStoreState(state => state.issue.isCategorySearchOpen);
  const setIsCategorySearchOpen = useStoreActions(actions => actions.issue.setIsCategorySearchOpen);
  const setCategorySearchText = useStoreActions(actions => actions.issue.setCategorySearchText);
  const { t: tIssues } = useTranslation('issues');
  const isSearchOpen = useNotificationState(state => state.isSearchOpen);
  const setIsSearchOpen = useNotificationAction(actions => actions.setIsSearchOpen);
  const unreadNotificationCount = useStoreState(state => state.notification.unreadNotificationCount);
  const unreadChatCount = useStoreState(state => state.chat.unreadNotificationCount);
  const netWorkStatus = useStoreState((state) => state.app.offline.status);
  const isTimeManagementPage = useStoreState((state) => state.app.appHeader.isTimeManagementPage);
  const isAddTimeForm = useStoreState((state) => state.app.appHeader.isAddTimeForm);
  const isGridView = useStoreState((state) => state.app.appHeader.isGridView);
  const openInFull = useStoreState((state) => state.app.appHeader.openInFull);
  
  const backLinkClickHandler = useCallback(() => {
    if (typeof backLink === 'string') {
      history.push(backLink);
    } else if (backLink) {
      backLink();
    }
  }, [backLink, history]);

  const menuClickHandler = useCallback(() => {
    setIsMenuOpen(true);
  }, [setIsMenuOpen]);

  const searchClickHandler = useCallback(() => {
    setIsSearchOpen(!isSearchOpen);
  }, [isSearchOpen, setIsSearchOpen]);

  const openCategorySearch = useCallback(() => {
    setIsCategorySearchOpen(true);
  }, [setIsCategorySearchOpen]);

  const closeCategorySearch = useCallback(() => {
    setIsCategorySearchOpen(false);
    setCategorySearchText('');
  }, [setIsCategorySearchOpen, setCategorySearchText]);

  const categorySearchVisible = Boolean(issueCategorySearch && isCategorySearchOpen);

  useDidMountEffect(() => {
    setStatusBarColor(getComputedStyle(document.documentElement).getPropertyValue('--color-header'));
  });

  return (
    <header className={`${classes['app-header']} ${parentFolderId && classes['light']}`}>
      <div className={classes['left-wrap']}>
        {(
          backLink
            ? <Icon name={backLinkIcon} onClick={backLinkClickHandler} />
            : <Icon name={menuIcon} onClick={menuClickHandler} />
        )}
      </div>

      {categorySearchVisible
        ? <IssueCategorySearch />
        : (title ? <Title/> : <img className={classes['logo']} src={bardeLogo} alt="Barde" />)}

      <div className={classes['right-wrap']}>
        {notification && netWorkStatus === 0 && ( 
          <>
            <LinkWrapper to={REQUESTS_ROUTES.chat}>
              <div className={classes['chat-icon-wrap']}>
                <Icon name={chatIcon} className={classNames(unreadChatCount ? classes['notification-badge-icon'] : "")}/>
              </div>
            </LinkWrapper>
            <LinkWrapper to={REQUESTS_ROUTES.notifications}>
              <div className={classes['chat-icon-wrap']}>
                <Icon name={notificationIcon} className={classNames(unreadNotificationCount ? classes['notification-badge-icon'] : "")}/>
              </div>
            </LinkWrapper>
          </>
        )}
        {issueCategorySearch && (
          categorySearchVisible ? (
            <button type="button" className={classes['close-search']} onClick={closeCategorySearch}>
              {tIssues('closeSearch')}
            </button>
          ) : (
            <Icon name={searchIcon} onClick={openCategorySearch}/>
          )
        )}
        {search && (
          <Icon name={searchIcon} onClick={searchClickHandler}/>
        )}
        {
          showUser ? (
            <UserDropdown/>
          ) : machineId ? (
            <Favorite id={machineId} type={'machine'} />
          ) : projectId ? (
            <Favorite id={projectId} type={'project'} /> 
          ) : isIssueDetailPage ? (
            <IssueOptions /> 
          ) : undefined
        }

      {isTimeManagementPage && (
        <LinkWrapper to={TIME_MANAGEMENT_ROUTES.details}>
          <Icon
            name={'calendar_today'}
            className={classes['calendarIcon']}
          />
        </LinkWrapper>
      )}

      {isGridView && (
        <LinkWrapper to={TIME_MANAGEMENT_ROUTES.activeTask}>
          <Icon
            name={'grid_view'}
            className={classes['gridView']}
          />
        </LinkWrapper>
      )}

      {isAddTimeForm && (
          <LinkWrapper to={TIME_MANAGEMENT_ROUTES.addTimeForm}>
            <Icon
              name={'add'}
              className={classes['add']}
            />
          </LinkWrapper>
        )}

      {openInFull && (
          <LinkWrapper to={TIME_MANAGEMENT_ROUTES.activeTime}>
            <Icon
              name={'open_in_full'}
              className={classes['open_in_full']}
            />
          </LinkWrapper>
        )}

      </div>
    </header>
  );
};
